// A publish the design checks refused: what failed, in the person's words and for Chat. The server
// names each failed check by code (`check`, `device`, `target`, and a `reason` when the shopper
// frame gave the design up); the codes pick the person's words, and go to Chat as they are.
import type { StorefrontCopyKey } from '@/i18n/ns/storefront';

type Copy = Readonly<Record<StorefrontCopyKey, string>>;
export type DesignFailure = {
  check: string;
  device: string;
  target: string;
  reason: string | null;
};

const text = (value: unknown, max: number) =>
  typeof value === 'string' ? value.slice(0, max) : '';

/** The failures the refusal carries, read defensively from the answer's `detail`. */
export function refusedChecks(detail: Record<string, unknown> | null): DesignFailure[] {
  const list = Array.isArray(detail?.failures) ? detail.failures : [];
  return list.slice(0, 6).map((item) => {
    const entry = (item ?? {}) as Record<string, unknown>;
    return {
      check: text(entry.check, 60),
      device: text(entry.device, 20),
      target: text(entry.target, 80),
      reason: text(entry.reason, 60) || null,
    };
  });
}

// The words for a check code, or for the reason a fallback names.
const LABEL = new Set(['ai_visible', 'ai_contrast']);
const REACH = new Set(['add_reachable', 'control_reachable', 'control_present']);

/** One plain line for the first failure it can describe, or nothing. */
export function plainReason(failures: DesignFailure[], copy: Copy): string | null {
  for (const { check, reason } of failures) {
    const code = check === 'design_fallback' && reason ? reason : check;
    if (LABEL.has(code)) return copy.designCheckLabel;
    if (REACH.has(code)) return copy.designCheckReach;
    if (code === 'minimum_text_size') return copy.designCheckSize;
    if (code === 'horizontal_overflow') return copy.designCheckOverflow;
    if (code === 'render_available') return copy.designCheckRender;
  }
  return null;
}

/** The facts Chat gets with the request: each failed check, where, and on which device. */
export function refusalFacts(failures: DesignFailure[]): string {
  const facts = failures
    .map(
      ({ check, device, target, reason }) =>
        `${check}${reason ? ` (${reason})` : ''}${target ? ` on ${target}` : ''}${device ? `, ${device}` : ''}`,
    )
    .join('; ');
  return facts.slice(0, 400);
}
